import React, { useState, useEffect } from 'react';

import { Dialog } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { FinancialPortal } from '@generated-gql/generated-types';

import InstitutionsFooter from '@app/(compass)/home/dashboard/settings/accounts/components/footers/institutions-footer/institutions-footer';
import { PlaidFooterInstitutionProvider } from './plaid-footer-institution-context';
import PlaidModalStepperWithProvider, {
  PlaidStep,
} from './plaid-modal-stepper';

import { delay } from './utils';
import { theme } from '@components/mui/ThemeProvider';
import { usePlaidModalVisibility } from './plaid-modal-visibility-context';

type Props = {
  /**
   * Callback function that is invoked when there is a modification to the linked
   * financial accounts via Plaid, such as adding, removing, or updating accounts.
   * You can use this callback to trigger a refetch of the linked financial accounts.
   *
   * The callback is called after a delay.
   */
  refetchPlaidLinkedAccounts?: () => void;
  /** Callback function after a Plaid account is unlinked. */
  onUnlinkAccountComplete?: () => void;
  plaidFooterProps?: {
    linkingState: {
      isLinking: boolean;
      numAccountsLinking: number;
    };
    actionRequiredState: {
      portal: FinancialPortal;
      isActionRequired: boolean;
      numAccountsActionRequired: number;
    };
  };
};

const PlaidModal = ({
  refetchPlaidLinkedAccounts,
  onUnlinkAccountComplete,
  plaidFooterProps,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { showPlaidVisibility, setShowPlaidVisibility, isOAuth } =
    usePlaidModalVisibility();

  useEffect(() => {
    if (isOAuth) {
      setShowPlaidVisibility({
        show: true,
        plaidStep: PlaidStep.PLAID_LINK,
        selectedPortal: null,
      });
    }
  }, [isOAuth, setShowPlaidVisibility]);

  useEffect(() => {
    setIsModalOpen(showPlaidVisibility.show);
  }, [showPlaidVisibility.show]);

  const handleCloseModal = () => {
    setIsModalOpen(false);

    // delays selectedPortal reset until modal is fully closed
    delay(
      () =>
        setShowPlaidVisibility({
          show: false,
          plaidStep: null,
          selectedPortal: null,
        }),
      300,
    );
  };

  const handleRefetch = () => {
    handleCloseModal();

    if (refetchPlaidLinkedAccounts) {
      delay(() => refetchPlaidLinkedAccounts(), 500);
    }
  };

  const handleUnlinkedAccounts = () => {
    handleCloseModal();

    if (onUnlinkAccountComplete) {
      onUnlinkAccountComplete();
    }
  };

  return (
    <>
      {plaidFooterProps && (
        <PlaidFooterInstitutionProvider
          linkingState={plaidFooterProps?.linkingState}
          actionRequiredState={plaidFooterProps?.actionRequiredState}
        >
          <InstitutionsFooter />
        </PlaidFooterInstitutionProvider>
      )}
      <Dialog
        maxWidth={'md'}
        open={isModalOpen}
        fullScreen={fullScreen}
        onClose={handleCloseModal}
      >
        <PlaidModalStepperWithProvider
          handleCloseModal={handleCloseModal}
          plaidModifiedLinkedAccounts={() => {
            handleRefetch();
          }}
          plaidUnlinkedAccounts={() => {
            handleUnlinkedAccounts();
          }}
          step={showPlaidVisibility.plaidStep}
        />
      </Dialog>
    </>
  );
};

export default PlaidModal;
